import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Star, 
  Calendar, 
  User, 
  FileText, 
  Shield, 
  Lock, 
  AlertTriangle, 
  CheckCircle, 
  Users, 
  Building, 
  Gavel, 
  FileCheck, 
  BookOpen,
  Upload,
  Plus,
  X,
  File
} from 'lucide-react'
import { dbHelpers } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useUser } from '../context/UserContext'


const PolicyLibrary = () => {
  const { user } = useAuth()
  const { canCreate, canDelete } = useUser()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedType, setSelectedType] = useState('all')
  const [sortBy, setSortBy] = useState('popular')
  const [uploadedDocuments, setUploadedDocuments] = useState([])
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'data-privacy',
    type: 'template',
    author: '',
    tags: ''
  })
  const fileInputRef = useRef(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  // Load documents from Supabase on component mount
  useEffect(() => {
    loadDocuments()
  }, [])

  const loadDocuments = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const documents = await dbHelpers.getDocuments({
        category: selectedCategory,
        type: selectedType,
        search: searchTerm
      })
      
      // Transform Supabase documents to match component format
      const transformedDocs = documents.map(doc => ({
        id: doc.id,
        title: doc.title,
        description: doc.description,
        category: doc.category,
        type: doc.type,
        fileSize: formatFileSize(doc.file_size),
        format: doc.file_format,
        downloads: doc.downloads,
        rating: doc.rating,
        lastUpdated: new Date(doc.created_at).toISOString().split('T')[0],
        author: 'User Upload',
        tags: doc.tags || [],
        featured: doc.is_featured,
        preview: doc.description,
        isUploaded: true,
        storagePath: doc.storage_path
      }))
      
      setUploadedDocuments(transformedDocs)
    } catch (error) {
      console.error('Error loading documents:', error)
    } finally {
      setLoading(false)
    }
  }

  const categories = [
    { id: 'all', label: 'All Categories', icon: FileText },
    { id: 'data-privacy', label: 'Data Privacy', icon: Shield },
    { id: 'cybersecurity', label: 'Cybersecurity', icon: Lock },
    { id: 'incident-response', label: 'Incident Response', icon: AlertTriangle },
    { id: 'compliance', label: 'Compliance', icon: CheckCircle },
    { id: 'training', label: 'Training & Awareness', icon: Users },
    { id: 'governance', label: 'IT Governance', icon: Building }
  ]

  const documentTypes = [
    { id: 'all', label: 'All Types' },
    { id: 'policy', label: 'Policies' },
    { id: 'template', label: 'Templates' },
    { id: 'checklist', label: 'Checklists' },
    { id: 'form', label: 'Forms' },
    { id: 'guide', label: 'Guidelines' }
  ]

  // File upload handling
  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedFile(file)
      // Auto-fill title if not already filled
      if (!uploadForm.title) {
        const fileName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
        setUploadForm(prev => ({ ...prev, title: fileName }))
      }
    }
  }

  const handleUploadSubmit = async (e) => {
    e.preventDefault()
    if (!selectedFile) {
      alert('Please select a file to upload')
      return
    }

    if (!user) {
      alert('You must be logged in to upload documents')
      return
    }

    try {
      setUploading(true)
      
      // Prepare metadata for Supabase
      const metadata = {
        title: uploadForm.title,
        description: uploadForm.description,
        category: uploadForm.category,
        type: uploadForm.type,
        tags: uploadForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        isPublic: true
      }

      // Upload to Supabase storage
      await dbHelpers.uploadDocument(selectedFile, metadata, user.id)
      
      // Reset form and close modal
      setUploadForm({
        title: '',
        description: '',
        category: 'data-privacy',
        type: 'template',
        author: '',
        tags: ''
      })
      setSelectedFile(null)
      setShowUploadModal(false)
      
      // Reload documents to show the new upload
      await loadDocuments()
      
      alert('Document uploaded successfully!')
    } catch (error) {
      console.error('Upload error:', error)
      alert('Error uploading document. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        let content = e.target.result
        
        // For binary files, provide a meaningful preview
        const fileType = file.type.toLowerCase()
        const fileName = file.name.toLowerCase()
        
        if (fileType.includes('pdf') || fileName.endsWith('.pdf')) {
          content = `PDF Document: ${file.name}\n\nThis is a PDF document that contains:\n- Document title: ${file.name.replace(/\.[^/.]+$/, '')}\n- File size: ${formatFileSize(file.size)}\n- Upload date: ${new Date().toLocaleDateString()}\n\nNote: This is a preview of the PDF file. The actual content would be displayed in a proper PDF viewer.`
        } else if (fileType.includes('word') || fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
          content = `Word Document: ${file.name}\n\nThis is a Microsoft Word document that contains:\n- Document title: ${file.name.replace(/\.[^/.]+$/, '')}\n- File size: ${formatFileSize(file.size)}\n- Upload date: ${new Date().toLocaleDateString()}\n\nNote: This is a preview of the Word document. The actual formatted content would be displayed in a proper document viewer.`
        } else if (fileType.includes('powerpoint') || fileName.endsWith('.pptx') || fileName.endsWith('.ppt')) {
          content = `PowerPoint Presentation: ${file.name}\n\nThis is a Microsoft PowerPoint presentation that contains:\n- Presentation title: ${file.name.replace(/\.[^/.]+$/, '')}\n- File size: ${formatFileSize(file.size)}\n- Upload date: ${new Date().toLocaleDateString()}\n\nNote: This is a preview of the PowerPoint presentation. The actual slides would be displayed in a proper presentation viewer.`
        }
        
        resolve(content)
      }
      reader.onerror = (e) => reject(e)
      
      // Read as text for text files, or provide preview for binary files
      const fileType = file.type.toLowerCase()
      const fileName = file.name.toLowerCase()
      
      if (fileType.includes('text') || fileName.endsWith('.txt') || 
          (!fileType.includes('pdf') && !fileType.includes('word') && !fileType.includes('powerpoint') &&
           !fileName.endsWith('.pdf') && !fileName.endsWith('.docx') && !fileName.endsWith('.doc') &&
           !fileName.endsWith('.pptx') && !fileName.endsWith('.ppt'))) {
        reader.readAsText(file)
      } else {
        // For binary files, we'll create a preview in the onload handler
        reader.readAsArrayBuffer(file)
      }
    })
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileFormat = (filename) => {
    const extension = filename.split('.').pop().toUpperCase()
    return extension || 'TXT'
  }

  const handleDeleteUploadedDoc = async (docId) => {
    if (!user) {
      alert('You must be logged in to delete documents')
      return
    }

    if (confirm('Are you sure you want to delete this document?')) {
      try {
        await dbHelpers.deleteDocument(docId, user.id)
        // Reload documents to reflect the deletion
        await loadDocuments()
        alert('Document deleted successfully!')
      } catch (error) {
        console.error('Delete error:', error)
        alert('Error deleting document. Please try again.')
      }
    }
  }

  // Download function for documents
  const handleDownload = async (doc) => {
    try {
      console.log('Starting download for:', doc.title)
      
      if (doc.isUploaded && doc.storagePath) {
        // Handle Supabase storage documents
        console.log('Downloading uploaded document from storage')
        const signedUrl = await dbHelpers.getDocumentUrl(doc.storagePath)
        
        if (!signedUrl) {
          throw new Error('Failed to get download URL')
        }
        
        // Use fetch to get the file and force download
        const response = await fetch(signedUrl)
        if (!response.ok) {
          throw new Error('Failed to fetch file')
        }
        
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        
        // Create download link
        const a = document.createElement('a')
        a.href = url
        a.download = doc.title.includes('.') ? doc.title : `${doc.title}.${doc.format.toLowerCase()}`
        a.style.display = 'none'
        
        // Append to body and trigger download
        document.body.appendChild(a)
        a.click()
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }, 100)
        
        // Increment download count
        await dbHelpers.incrementDocumentDownloads(doc.id)
        
        // Reload documents to update download count
        await loadDocuments()
        
        console.log('Upload document download completed')
      } else {
        // Handle pre-existing documents (generate content)
        console.log('Generating and downloading document content')
        let content = generateDocumentContent(doc)
        
        // Create blob with proper MIME type
        const mimeTypes = {
          'PDF': 'application/pdf',
          'DOCX': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'PPTX': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'TXT': 'text/plain'
        }
        
        const mimeType = mimeTypes[doc.format.toUpperCase()] || 'text/plain'
        const blob = new Blob([content], { type: mimeType })
        const url = URL.createObjectURL(blob)
        
        // Create download link
        const a = document.createElement('a')
        a.href = url
        a.download = `${doc.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${doc.format.toLowerCase()}`
        a.style.display = 'none'
        
        // Append to body and trigger download
        document.body.appendChild(a)
        a.click()
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }, 100)
        
        console.log('Generated document download completed')
      }
      
      // Show success message
      alert(`Download started: ${doc.title}`)
      console.log(`Downloaded: ${doc.title}`)
    } catch (error) {
      console.error('Download error:', error)
      alert('Error downloading document. Please try again.')
    }
  }

  // Enhanced preview function for documents
  const handlePreview = async (doc) => {
    try {
      console.log('Starting preview for:', doc.title)
      
      if (doc.isUploaded && doc.storagePath) {
        // For uploaded documents, handle different file types
        console.log('Previewing uploaded document from storage')
        const signedUrl = await dbHelpers.getDocumentUrl(doc.storagePath)
        
        if (!signedUrl) {
          throw new Error('Failed to get preview URL')
        }
        
        // Check file extension to determine preview method
        const fileExtension = doc.title.split('.').pop()?.toLowerCase() || doc.format.toLowerCase()
        
        if (['pdf'].includes(fileExtension)) {
          // For PDFs, open directly in new tab
          window.open(signedUrl, '_blank')
        } else if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(fileExtension)) {
          // For images, create an image preview
          const previewWindow = window.open('', '_blank')
          if (previewWindow) {
            previewWindow.document.write(`
              <!DOCTYPE html>
              <html>
              <head>
                <title>Preview: ${doc.title}</title>
                <style>
                  body { margin: 0; padding: 20px; background: #f0f0f0; text-align: center; }
                  img { max-width: 100%; max-height: 90vh; box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
                  .header { margin-bottom: 20px; }
                </style>
              </head>
              <body>
                <div class="header">
                  <h2>${doc.title}</h2>
                </div>
                <img src="${signedUrl}" alt="${doc.title}" />
              </body>
              </html>
            `)
            previewWindow.document.close()
            
            // Add event listener for download button without inline handlers
            const downloadBtn = previewWindow.document.getElementById(`download-btn-${doc.id}`)
            if (downloadBtn) {
              downloadBtn.addEventListener('click', () => {
                window.postMessage({action: 'download', docId: doc.id}, '*')
              })
            }
          }
        } else {
          // For other file types, show file information and preview option
          const previewWindow = window.open('', '_blank')
          if (previewWindow) {
            previewWindow.document.write(`
              <!DOCTYPE html>
              <html>
              <head>
                <title>Preview: ${doc.title}</title>
                <meta charset="UTF-8">
                <style>
                  body {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    line-height: 1.6;
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 20px;
                    background-color: #f8f9fa;
                  }
                  .header {
                    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
                    color: white;
                    padding: 25px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    text-align: center;
                  }
                  .file-info {
                    background: white;
                    padding: 30px;
                    border-radius: 8px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    margin-bottom: 20px;
                  }
                  .btn {
                    display: inline-block;
                    padding: 12px 24px;
                    margin: 10px;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    text-decoration: none;
                    font-size: 16px;
                    transition: background-color 0.3s;
                  }
                  .btn-primary {
                    background: #059669;
                    color: white;
                  }
                  .btn-primary:hover {
                    background: #047857;
                  }
                  .btn-secondary {
                    background: #6b7280;
                    color: white;
                  }
                  .btn-secondary:hover {
                    background: #4b5563;
                  }
                </style>
              </head>
              <body>
                <div class="header">
                  <h1>${doc.title}</h1>
                  <p>${doc.description}</p>
                </div>
                <div class="file-info">
                  <h3>File Information</h3>
                  <p><strong>File Name:</strong> ${doc.title}</p>
                  <p><strong>Category:</strong> ${doc.category}</p>
                  <p><strong>Type:</strong> ${doc.type}</p>
                  <p><strong>Author:</strong> ${doc.author}</p>
                  <p><strong>Last Updated:</strong> ${new Date(doc.lastUpdated).toLocaleDateString()}</p>
                  <p><strong>Downloads:</strong> ${doc.downloads.toLocaleString()}</p>
                  <hr>
                  <p><strong>Note:</strong> This file type cannot be previewed directly in the browser. You can download it to view the content.</p>
                </div>
                <div style="text-align: center;">
                  <a href="${signedUrl}" target="_blank" class="btn btn-secondary">Open File in New Tab</a>
                  <button class="btn btn-primary" data-doc-id="${doc.id}" id="download-btn-${doc.id}">Download File</button>
                </div>
              </body>
              </html>
            `)
            previewWindow.document.close()
          }
        }
        
        console.log('Uploaded document preview completed')
        return
      }
      
      // For pre-existing documents, generate content and show in preview window
      console.log('Generating preview for pre-existing document')
      let content = generateDocumentContent(doc)
      
      const previewWindow = window.open('', '_blank')
      
      // Check if window was successfully opened
      if (!previewWindow || !previewWindow.document) {
        alert('Unable to open preview window. Please check your browser\'s popup blocker settings.')
        return
      }
      
      previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Preview: ${doc.title}</title>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              line-height: 1.6;
              max-width: 900px;
              margin: 0 auto;
              padding: 20px;
              background-color: #f8f9fa;
            }
            .header {
              background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
              color: white;
              padding: 25px;
              border-radius: 8px;
              margin-bottom: 20px;
              box-shadow: 0 4px 15px rgba(30, 64, 175, 0.3);
            }
            .header h1 {
              margin: 0 0 10px 0;
              font-size: 24px;
            }
            .header p {
              margin: 0;
              opacity: 0.9;
              font-size: 16px;
            }
            .content {
              background: white;
              padding: 30px;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              white-space: pre-line;
            }
            .meta {
              background: #e5e7eb;
              padding: 15px;
              border-radius: 6px;
              margin-bottom: 20px;
              font-size: 14px;
            }
            .download-btn {
              background: #059669;
              color: white;
              padding: 10px 20px;
              border: none;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              margin-top: 20px;
            }
            .download-btn:hover {
              background: #047857;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${doc.title}</h1>
            <p>${doc.description}</p>
          </div>
          <div class="meta">
            <strong>Category:</strong> ${doc.category} | 
            <strong>Type:</strong> ${doc.type} | 
            <strong>Author:</strong> ${doc.author} | 
            <strong>Last Updated:</strong> ${new Date(doc.lastUpdated).toLocaleDateString()} | 
            <strong>Downloads:</strong> ${doc.downloads.toLocaleString()}
          </div>
          <div class="content">${content}</div>
          <button class="download-btn" data-doc-id="${doc.id}" id="download-btn-${doc.id}">Download This Document</button>
        </body>
        </html>
      `)
      
      previewWindow.document.close()
      
      // Add event listener for download button without inline handlers
      const downloadBtn = previewWindow.document.getElementById(`download-btn-${doc.id}`)
      if (downloadBtn) {
        downloadBtn.addEventListener('click', () => {
          window.postMessage({action: 'download', docId: doc.id}, '*')
        })
      }
      
      // Listen for download message from preview window
      window.addEventListener('message', (event) => {
        if (event.data.action === 'download' && event.data.docId === doc.id) {
          handleDownload(doc)
        }
      })
      
      console.log('Generated document preview completed')
      console.log(`Previewing: ${doc.title}`)
    } catch (error) {
      console.error('Preview error:', error)
      alert('Error opening document preview. Please try again.')
    }
  }

  // Generate document content based on document type
  const generateDocumentContent = (doc) => {
    // If it's an uploaded document, return its actual content
    if (doc.isUploaded && doc.content) {
      return doc.content
    }
    
    // Default content for pre-existing documents
    switch (doc.id) {
      case 1: // Data Privacy Policy Template
        return `DATA PRIVACY POLICY TEMPLATE

Compliant with Republic Act No. 10173 (Data Privacy Act of 2012)

1. POLICY STATEMENT
This organization is committed to protecting the privacy and security of personal data in accordance with the Data Privacy Act of 2012 and its implementing rules and regulations.

2. SCOPE
This policy applies to all personal data collected, processed, and stored by [Organization Name].

3. DATA COLLECTION PRINCIPLES
- Transparency: Data subjects will be informed about data collection
- Legitimate Purpose: Data will only be collected for specified, legitimate purposes
- Proportionality: Only necessary data will be collected

4. DATA SUBJECT RIGHTS
- Right to be informed
- Right to access
- Right to object
- Right to erasure or blocking
- Right to rectification
- Right to data portability
- Right to damages

5. DATA SECURITY MEASURES
- Physical safeguards
- Technical safeguards
- Organizational safeguards

6. DATA BREACH RESPONSE
- Immediate containment
- Assessment of breach
- Notification to NPC within 72 hours
- Notification to affected data subjects

7. CONTACT INFORMATION
Data Protection Officer: [Name]
Email: [Email]
Phone: [Phone]

[Organization Name]
[Date]

Note: This is a template document. Please customize according to your organization's specific needs and consult with legal counsel.`

      case 2: // Cybersecurity Incident Response Plan
        return `CYBERSECURITY INCIDENT RESPONSE PLAN

For Government Agencies
DICT Cybersecurity Bureau

TABLE OF CONTENTS
1. Introduction
2. Incident Response Team
3. Incident Classification
4. Response Procedures
5. Communication Protocols
6. Recovery Procedures
7. Post-Incident Activities

1. INTRODUCTION
This plan provides a structured approach to handling cybersecurity incidents to minimize impact and ensure rapid recovery.

2. INCIDENT RESPONSE TEAM
- Incident Commander
- Technical Lead
- Communications Lead
- Legal Counsel
- Management Representative

3. INCIDENT CLASSIFICATION
Severity Levels:
- Critical: System compromise affecting critical operations
- High: Significant security breach with potential data loss
- Medium: Security incident with limited impact
- Low: Minor security event requiring monitoring

4. RESPONSE PROCEDURES

Phase 1: Preparation
- Maintain incident response capabilities
- Train response team
- Establish communication channels

Phase 2: Detection and Analysis
- Monitor security events
- Analyze potential incidents
- Determine incident scope

Phase 3: Containment, Eradication, and Recovery
- Contain the incident
- Eliminate the threat
- Restore systems to normal operation

Phase 4: Post-Incident Activity
- Document lessons learned
- Update procedures
- Conduct training

5. COMMUNICATION PROTOCOLS
- Internal notifications
- External notifications (DICT, NPC, etc.)
- Public communications
- Media relations

6. RECOVERY PROCEDURES
- System restoration
- Data recovery
- Service resumption
- Monitoring for reoccurrence

7. POST-INCIDENT ACTIVITIES
- Incident documentation
- Lessons learned analysis
- Procedure updates
- Training improvements

EMERGENCY CONTACTS:
DICT Cybersecurity Hotline: [Number]
National CERT: [Number]
Local IT Support: [Number]

This document is classified as CONFIDENTIAL and should be distributed only to authorized personnel.`

      case 3: // Employee Consent Form
        return `EMPLOYEE CONSENT FORM
For Collection and Processing of Personal Data

Compliant with Republic Act No. 10173 (Data Privacy Act of 2012)

[Organization Name]
[Address]
[Contact Information]

EMPLOYEE INFORMATION:
Name: _________________________
Position: ______________________
Department: ___________________
Employee ID: __________________
Date: _________________________

CONSENT STATEMENT:
I, _________________________, hereby give my free, voluntary, and informed consent to [Organization Name] to collect, process, and store my personal data for the following purposes:

1. EMPLOYMENT-RELATED PURPOSES:
   ☐ Personnel records management
   ☐ Payroll processing
   ☐ Benefits administration
   ☐ Performance evaluation
   ☐ Training and development
   ☐ Health and safety compliance

2. LEGAL AND REGULATORY COMPLIANCE:
   ☐ Tax reporting (BIR)
   ☐ Social security (SSS/GSIS)
   ☐ Health insurance (PhilHealth)
   ☐ Housing fund (Pag-IBIG)
   ☐ Other government requirements

3. TYPES OF PERSONAL DATA:
   ☐ Basic personal information (name, address, contact details)
   ☐ Government-issued IDs
   ☐ Educational background
   ☐ Employment history
   ☐ Financial information
   ☐ Health information (if applicable)
   ☐ Emergency contact information

4. DATA SHARING:
I understand that my personal data may be shared with:
   ☐ Government agencies (as required by law)
   ☐ Third-party service providers (payroll, benefits)
   ☐ Healthcare providers (if applicable)
   ☐ Legal counsel (if necessary)

5. DATA RETENTION:
I understand that my personal data will be retained for the duration of my employment and for [X] years after termination, or as required by law.

6. MY RIGHTS:
I understand that I have the right to:
- Access my personal data
- Correct inaccurate data
- Object to processing
- Request deletion of data
- File complaints with the National Privacy Commission

7. WITHDRAWAL OF CONSENT:
I understand that I may withdraw this consent at any time, subject to legal and contractual obligations.

DATA PROTECTION OFFICER:
Name: _________________________
Email: ________________________
Phone: _______________________

Employee Signature: _________________________ Date: _________

Witness Signature: __________________________ Date: _________

For Office Use Only:
Received by: _____________________ Date: _________
Filed in: _______________________`

      case 6: // Cybersecurity Training Program Template
        return `CYBERSECURITY TRAINING PROGRAM TEMPLATE

For Government Offices
DICT Training Division

PROGRAM OVERVIEW
Duration: 8 hours (can be conducted over 2 days)
Target Audience: All government employees
Delivery Method: Instructor-led with hands-on exercises

LEARNING OBJECTIVES
By the end of this training, participants will be able to:
1. Identify common cybersecurity threats
2. Implement basic security practices
3. Respond appropriately to security incidents
4. Protect sensitive government data
5. Comply with cybersecurity policies

MODULE 1: INTRODUCTION TO CYBERSECURITY (1 hour)
- What is cybersecurity?
- Why it matters for government
- Current threat landscape
- Legal and regulatory requirements

MODULE 2: COMMON THREATS AND ATTACKS (1.5 hours)
- Phishing and social engineering
- Malware and ransomware
- Password attacks
- Physical security threats
- Insider threats

MODULE 3: EMAIL AND WEB SECURITY (1.5 hours)
- Safe email practices
- Identifying suspicious emails
- Web browsing security
- Download safety
- Social media risks

MODULE 4: PASSWORD SECURITY (1 hour)
- Creating strong passwords
- Password management tools
- Multi-factor authentication
- Account security

MODULE 5: DATA PROTECTION (1.5 hours)
- Classification of government data
- Data handling procedures
- Secure storage and transmission
- Privacy considerations
- Data backup and recovery

MODULE 6: MOBILE AND REMOTE WORK SECURITY (1 hour)
- Mobile device security
- Remote access best practices
- Public Wi-Fi risks
- BYOD policies

MODULE 7: INCIDENT RESPONSE (30 minutes)
- Recognizing security incidents
- Reporting procedures
- Initial response steps
- Recovery actions

MODULE 8: HANDS-ON EXERCISES (30 minutes)
- Phishing simulation
- Password strength testing
- Security tool demonstration
- Q&A session

TRAINING MATERIALS NEEDED:
- Presentation slides
- Handout materials
- Computers/laptops for exercises
- Projector and screen
- Flipchart and markers

ASSESSMENT:
- Pre-training quiz
- Module quizzes
- Final assessment
- Practical exercises
- Training evaluation form

CERTIFICATION:
Participants who complete the training and pass the assessment will receive a Certificate of Completion.

FOLLOW-UP:
- Quarterly refresher sessions
- Security awareness newsletters
- Phishing simulation exercises
- Policy updates

TRAINER QUALIFICATIONS:
- Cybersecurity certification (CISSP, CISM, etc.)
- Government training experience
- Adult learning principles knowledge

CONTACT INFORMATION:
DICT Training Division
Email: training@dict.gov.ph
Phone: [Phone Number]

This template can be customized based on specific organizational needs and threat environment.`

      case 8: // Remote Work Security Guidelines
        return `REMOTE WORK SECURITY GUIDELINES

For Government Employees
DICT Cybersecurity Bureau

TABLE OF CONTENTS
1. Introduction
2. General Security Principles
3. Home Office Setup
4. Network Security
5. Device Security
6. Data Protection
7. Communication Security
8. Incident Reporting
9. Compliance Requirements

1. INTRODUCTION
These guidelines provide essential security measures for government employees working remotely to protect sensitive information and maintain cybersecurity.

2. GENERAL SECURITY PRINCIPLES
- Maintain the same security standards as in the office
- Protect government data at all times
- Report security incidents immediately
- Keep software and systems updated
- Use only approved tools and services

3. HOME OFFICE SETUP
Physical Security:
- Work in a private, secure area
- Lock screens when away from desk
- Secure physical documents
- Ensure privacy during video calls
- Install security cameras if handling sensitive data

Environmental Considerations:
- Adequate lighting
- Ergonomic workspace
- Minimal distractions
- Professional background for video calls

4. NETWORK SECURITY
Internet Connection:
- Use secure, encrypted Wi-Fi (WPA3 preferred)
- Avoid public Wi-Fi for work activities
- Change default router passwords
- Enable router firewall
- Regularly update router firmware

VPN Usage:
- Always connect through approved VPN
- Verify VPN connection before accessing systems
- Disconnect VPN when not working
- Report VPN connection issues immediately

5. DEVICE SECURITY
Government-Issued Devices:
- Use only for official business
- Install approved software only
- Enable automatic screen lock
- Use strong authentication
- Keep devices physically secure

Personal Devices (if permitted):
- Install required security software
- Separate work and personal data
- Enable device encryption
- Use mobile device management (MDM)
- Regular security updates

6. DATA PROTECTION
Data Classification:
- Understand data classification levels
- Handle data according to classification
- Store data in approved locations only
- Encrypt sensitive data
- Secure data transmission

Backup and Storage:
- Use approved cloud storage only
- Regular data backups
- Secure local storage
- Proper data disposal
- Version control for documents

7. COMMUNICATION SECURITY
Email Security:
- Use official email accounts only
- Verify sender identity
- Encrypt sensitive emails
- Avoid personal email for work
- Report suspicious emails

Video Conferencing:
- Use approved platforms only
- Enable waiting rooms
- Use meeting passwords
- Mute when not speaking
- Record meetings securely

Instant Messaging:
- Use approved messaging platforms
- Avoid sharing sensitive information
- Enable encryption
- Log out after use
- Report unauthorized contacts

8. INCIDENT REPORTING
Security Incidents:
- Report immediately to IT security team
- Document incident details
- Preserve evidence
- Follow incident response procedures
- Cooperate with investigation

Contact Information:
- IT Helpdesk: [Phone/Email]
- Security Team: [Phone/Email]
- Emergency Contact: [Phone/Email]

9. COMPLIANCE REQUIREMENTS
Regular Requirements:
- Complete security training
- Update security software
- Review and acknowledge policies
- Participate in security assessments
- Maintain security certifications

Monitoring and Auditing:
- System access logging
- Regular security scans
- Compliance audits
- Performance monitoring
- Security incident tracking

CONSEQUENCES OF NON-COMPLIANCE:
- Disciplinary action
- Loss of remote work privileges
- Security clearance review
- Legal consequences
- Termination of employment

ACKNOWLEDGMENT:
I have read, understood, and agree to comply with these Remote Work Security Guidelines.

Employee Name: _________________________
Signature: _____________________________
Date: _________________________________

Supervisor Name: _______________________
Signature: _____________________________
Date: _________________________________

For questions or clarifications, contact:
DICT Cybersecurity Bureau
Email: cybersecurity@dict.gov.ph
Phone: [Phone Number]

Document Version: 2.0
Last Updated: January 2024
Next Review: July 2024`

      default:
        return `${doc.title}\n\n${doc.description}\n\nThis is a sample document template. In a production environment, this would contain the actual document content.\n\nDocument Details:\n- Category: ${doc.category}\n- Type: ${doc.type}\n- Author: ${doc.author}\n- Last Updated: ${doc.lastUpdated}\n\nFor more information, please contact the document author or your IT administrator.`
    }
  }

  // Static policy documents removed - only showing documents from storage

  // Only use uploaded documents from storage
  const filteredDocuments = uploadedDocuments.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory
    const matchesType = selectedType === 'all' || doc.type === selectedType
    
    return matchesSearch && matchesCategory && matchesType
  })

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b.downloads - a.downloads
      case 'recent':
        return new Date(b.lastUpdated) - new Date(a.lastUpdated)
      case 'rating':
        return b.rating - a.rating
      case 'alphabetical':
        return a.title.localeCompare(b.title)
      default:
        return 0
    }
  })

  const featuredDocuments = uploadedDocuments.filter(doc => doc.featured)

  const getTypeIcon = (type) => {
    switch (type) {
      case 'policy': return Gavel
      case 'template': return FileText
      case 'checklist': return FileCheck
      case 'form': return BookOpen
      case 'guide': return BookOpen
      default: return FileText
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'policy': return 'bg-security-100 text-security-700'
      case 'template': return 'bg-cyber-100 text-cyber-700'
      case 'checklist': return 'bg-success-100 text-success-700'
      case 'form': return 'bg-warning-100 text-warning-700'
      case 'guide': return 'bg-gov-100 text-gov-700'
      default: return 'bg-gov-100 text-gov-700'
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-cyber-600 rounded-full flex items-center justify-center">
              <FileText className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-display font-bold text-gov-800 mb-4">
            Policy & Template Library
          </h1>
          <p className="text-xl text-gov-600 max-w-3xl mx-auto mb-6">
            Access ready-to-use policies, templates, and guidelines designed specifically for Philippine government offices. 
            All documents are compliant with local laws and regulations.
          </p>
          
          {/* Upload Button */}
          {canCreate() && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowUploadModal(true)}
              className="bg-cyber-600 hover:bg-cyber-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 mx-auto transition-colors duration-200"
            >
              <Upload className="w-5 h-5" />
              <span>Upload Document</span>
            </motion.button>
          )}
        </motion.div>

        {/* Featured Documents */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-semibold text-gov-800 mb-6 flex items-center space-x-2">
            <Star className="w-6 h-6 text-warning-500" />
            <span>Featured Documents</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredDocuments.map((doc) => {
              const TypeIcon = getTypeIcon(doc.type)
              return (
                <motion.div
                  key={doc.id}
                  whileHover={{ y: -5 }}
                  className="card group cursor-pointer"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-cyber-100 rounded-lg flex items-center justify-center">
                      <TypeIcon className="w-5 h-5 text-cyber-600" />
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(doc.type)}`}>
                      {doc.type}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gov-800 mb-2 group-hover:text-cyber-600 transition-colors duration-200">
                    {doc.title}
                  </h3>
                  <p className="text-sm text-gov-600 mb-4 line-clamp-2">
                    {doc.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gov-500 mb-4">
                    <span>{doc.downloads.toLocaleString()} downloads</span>
                    <div className="flex items-center space-x-1">
                      <Star className="w-3 h-3 text-warning-500 fill-current" />
                      <span>{doc.rating}</span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDownload(doc)}
                      className="flex-1 bg-cyber-600 hover:bg-cyber-700 text-white text-xs py-2 px-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-1"
                    >
                      <Download className="w-3 h-3" />
                      <span>Download</span>
                    </button>
                    <button
                      onClick={() => handlePreview(doc)}
                      className="flex-1 bg-gov-100 hover:bg-gov-200 text-gov-700 text-xs py-2 px-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-1"
                    >
                      <Eye className="w-3 h-3" />
                      <span>Preview</span>
                    </button>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-8"
        >
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gov-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search policies, templates, and documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input-field"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Type Filter */}
            <div>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="input-field"
              >
                {documentTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Sort Options */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gov-200">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gov-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-gov-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cyber-500"
              >
                <option value="popular">Most Popular</option>
                <option value="recent">Most Recent</option>
                <option value="rating">Highest Rated</option>
                <option value="alphabetical">Alphabetical</option>
              </select>
            </div>
            <span className="text-sm text-gov-600">
              {sortedDocuments.length} documents found
            </span>
          </div>
        </motion.div>

        {/* Document Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-600 mx-auto mb-4"></div>
              <p className="text-gov-600">Loading documents...</p>
            </div>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            <AnimatePresence>
              {sortedDocuments.map((doc) => {
              const TypeIcon = getTypeIcon(doc.type)
              return (
                <motion.div
                  key={doc.id}
                  variants={itemVariants}
                  layout
                  className="card group hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-cyber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TypeIcon className="w-6 h-6 text-cyber-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gov-800 group-hover:text-cyber-600 transition-colors duration-200">
                          {doc.title}
                        </h3>
                        {doc.featured && (
                          <Star className="w-4 h-4 text-warning-500 fill-current flex-shrink-0 ml-2" />
                        )}
                      </div>
                      
                      <p className="text-sm text-gov-600 mb-3 line-clamp-2">
                        {doc.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-1 mb-3">
                        {doc.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="px-2 py-1 text-xs bg-gov-100 text-gov-700 rounded">
                            {tag}
                          </span>
                        ))}
                        {doc.tags.length > 3 && (
                          <span className="px-2 py-1 text-xs bg-gov-100 text-gov-700 rounded">
                            +{doc.tags.length - 3} more
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-gov-500 mb-4">
                        <div className="flex items-center space-x-4">
                          <span className={`px-2 py-1 rounded ${getTypeColor(doc.type)}`}>
                            {doc.type.toUpperCase()}
                          </span>
                          <span>{doc.fileSize} • {doc.format}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="w-3 h-3 text-warning-500 fill-current" />
                          <span>{doc.rating}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-gov-500 mb-4">
                        <div className="flex items-center space-x-1">
                          <User className="w-3 h-3" />
                          <span>{doc.author}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{new Date(doc.lastUpdated).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button 
                          className="btn-primary flex-1 flex items-center justify-center space-x-2 text-sm py-2"
                          onClick={() => handleDownload(doc)}
                        >
                          <Download size={14} />
                          <span>Download</span>
                        </button>
                        <button 
                          className="btn-secondary flex items-center justify-center space-x-2 text-sm py-2 px-3"
                          onClick={() => handlePreview(doc)}
                        >
                          <Eye size={14} />
                          <span>Preview</span>
                        </button>
                        {doc.isUploaded && canDelete() && (
                          <button 
                            className="bg-red-500 hover:bg-red-600 text-white flex items-center justify-center text-sm py-2 px-3 rounded-lg transition-colors duration-200"
                            onClick={() => handleDeleteUploadedDoc(doc.id)}
                            title="Delete Document"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gov-200">
                        <span className="text-xs text-gov-500">
                          {doc.downloads.toLocaleString()} downloads
                        </span>
                        <div className="flex items-center space-x-1 text-xs text-gov-500">
                          <Download className="w-3 h-3" />
                          <span>Free Download</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </motion.div>
        )}

        {/* No Results */}
        {sortedDocuments.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <FileText className="w-16 h-16 text-gov-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gov-600 mb-2">No documents found</h3>
            <p className="text-gov-500 mb-4">Try adjusting your search terms or filters.</p>
            <button
              onClick={() => {
                setSearchTerm('')
                setSelectedCategory('all')
                setSelectedType('all')
              }}
              className="btn-secondary"
            >
              Clear Filters
            </button>
          </motion.div>
        )}

        {/* Upload Modal */}
        <AnimatePresence>
          {showUploadModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowUploadModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-semibold text-gov-800 flex items-center space-x-2">
                      <Upload className="w-6 h-6 text-cyber-600" />
                      <span>Upload Document</span>
                    </h2>
                    <button
                      onClick={() => setShowUploadModal(false)}
                      className="text-gov-400 hover:text-gov-600 transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <form onSubmit={handleUploadSubmit} className="space-y-6">
                    {/* File Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Select File *
                      </label>
                      <div className="border-2 border-dashed border-gov-300 rounded-lg p-6 text-center hover:border-cyber-400 transition-colors">
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleFileSelect}
                          className="hidden"
                          accept=".txt,.doc,.docx,.pdf,.ppt,.pptx"
                        />
                        <File className="w-12 h-12 text-gov-400 mx-auto mb-4" />
                        <p className="text-gov-600 mb-2">
                          {selectedFile ? selectedFile.name : 'Click to select a file or drag and drop'}
                        </p>
                        <p className="text-sm text-gov-500 mb-4">
                          Supported formats: TXT, DOC, DOCX, PDF, PPT, PPTX
                        </p>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="btn-secondary"
                        >
                          Choose File
                        </button>
                      </div>
                    </div>

                    {/* Document Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gov-700 mb-2">
                          Document Title *
                        </label>
                        <input
                          type="text"
                          value={uploadForm.title}
                          onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                          className="input-field"
                          placeholder="Enter document title"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gov-700 mb-2">
                          Author
                        </label>
                        <input
                          type="text"
                          value={uploadForm.author}
                          onChange={(e) => setUploadForm(prev => ({ ...prev, author: e.target.value }))}
                          className="input-field"
                          placeholder="Enter author name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Description *
                      </label>
                      <textarea
                        value={uploadForm.description}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                        className="input-field"
                        rows={3}
                        placeholder="Enter document description"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gov-700 mb-2">
                          Category *
                        </label>
                        <select
                          value={uploadForm.category}
                          onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                          className="input-field"
                          required
                        >
                          {categories.filter(cat => cat.id !== 'all').map(category => (
                            <option key={category.id} value={category.id}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gov-700 mb-2">
                          Document Type *
                        </label>
                        <select
                          value={uploadForm.type}
                          onChange={(e) => setUploadForm(prev => ({ ...prev, type: e.target.value }))}
                          className="input-field"
                          required
                        >
                          {documentTypes.filter(type => type.id !== 'all').map(type => (
                            <option key={type.id} value={type.id}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Tags (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={uploadForm.tags}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                        className="input-field"
                        placeholder="e.g., Privacy, Security, Template"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gov-200">
                      <button
                        type="button"
                        onClick={() => setShowUploadModal(false)}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!selectedFile || !uploadForm.title || !uploadForm.description || uploading}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            <span>Upload Document</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default PolicyLibrary